import { useEffect } from "react";
import { FormActions } from "../../components/common/FormActions";
import FormFields from "../../components/common/FormFields";
import { IFormSection, TFormHookParams } from "../../types/common";
import {
  createColumn,
  createViewAction,
  getRegister,
  formatDateTime,
  numberFormat,
  createRequestReversalAction,
  beautify,
  formatAmount,
} from "../../utils/helpers";
import { URL_ADVANCES_TRANSACTIONS } from "../../utils/urls";
import useGet from "../common/useGet";
import { API_ADVANCES_TRANSACTIONS } from "../../utils/apis";
import { BooleanTags } from "../../components/common/registerUtils";
import { IsAccountant, IsSuperUser } from "../../utils/permissions";

export const useAdvancesTransactions = () => {
  const isSupper = IsSuperUser();
  const isAccountant = IsAccountant();
  const tableColumns = () => [
    createColumn("car_number", true, "Car Number"),
    createColumn("phone_number", true, "Phone Number"),
    createColumn("amount", false, "Amount"),
    // createColumn("balance_after", true, "Balance After"),
    createColumn("advance_balance", false, "Current Balance"),
    createColumn("advance_active", false, "Advance Active"),
    createColumn("booking_number", false, "Booking Number"),
    createColumn("customer_name", false, "Customer Name"),
    createColumn("txn_type", true, "Transaction Type"),
    createColumn("parking_name", false, "Parking Name"),
    createColumn("parking_start_time", { type: "date" }, "Start Time"),
    createColumn("parking_end_time", { type: "date" }, "End Time"),
    createColumn("created_at", { type: "date" }, "Created At"),
    createColumn("actions", false),
  ];

  const tableData = (data: any) => {
    if (!data) return [];

    return data.map((item: any) => {
      const transactionTypeFormatted = beautify(item?.txn_type);
      const formattedAmount = formatAmount(item?.amount);
      return {
        ...item,
        amount: numberFormat(item?.amount || 0),
        advance_balance: numberFormat(item?.advance_balance || 0),
        balance_after: numberFormat(item?.balance_after || 0),
        advance_active: <BooleanTags status={item?.advance_active} />,
        txn_type:
          item?.txn_type
            ?.replace(/_/g, " ")
            ?.replace(/\b\w/g, (l: string) => l.toUpperCase()) || "N/A",
        car_number: item?.car_number || "N/A",
        created_at: formatDateTime(item?.created_at),
        parking_start_time: formatDateTime(item?.parking_start_time),
        parking_end_time: formatDateTime(item?.parking_end_time),
        actions: (
          <FormActions
            actions={[
              createViewAction(URL_ADVANCES_TRANSACTIONS, item?.id),
              ...((isSupper || isAccountant) && item?.txn_type === "pre_payment"
                ? [
                    createRequestReversalAction(
                      `${API_ADVANCES_TRANSACTIONS}${item?.id}/request-reversal/`,
                      `of ${formattedAmount} for ${transactionTypeFormatted} from ${item?.phone_number}`,
                      true,
                    ),
                  ]
                : []),
            ]}
          />
        ),
      };
    });
  };

  const viewData = (item: any) => {
    // Handle case where item is undefined or null
    if (!item) {
      return {
        sections: [
          {
            title: "Error",
            fields: {
              message: "No data available",
            },
          },
        ],
      };
    }

    return {
      sections: [
        {
          title: "Transaction Details",
          fields: {
            phone_number: item?.phone_number || "N/A",
            amount: item?.amount
              ? `UGX ${item.amount.toLocaleString()}`
              : "UGX 0",
            txn_type:
              item?.txn_type
                ?.replace(/_/g, " ")
                ?.replace(/\b\w/g, (l: string) => l.toUpperCase()) || "N/A",
            car_number: item?.car_number || "N/A",
            advance_balance: `UGX ${item?.advance_balance?.toLocaleString() || 0}`,
            balance_after: `UGX ${item?.balance_after?.toLocaleString() || 0}`,
            advance_active: <BooleanTags status={item?.advance_active} />,
            parked_car: item?.parked_car || "N/A",
          },
        },
        {
          title: "System Information",
          fields: {
            transaction_id: item?.id || "N/A",
            created_at: item?.created_at
              ? formatDateTime(item.created_at)
              : "N/A",
          },
        },
      ],
    };
  };

  const activityFilter = "advance_transactions";
  const tableTitle = "E-Coupon Transactions";

  return {
    tableData,
    tableColumns,
    tableTitle,
    viewData,
    activityFilter,
  };
};

export const useAdvancesTransactionsForm = ({ id }: TFormHookParams = {}) => {
  // Get existing transactions data
  const { loadData, loading, data } = useGet();

  useEffect(() => {
    loadData({ api: API_ADVANCES_TRANSACTIONS });
  }, []);

  // Check if this is an edit operation (id exists)
  const isEdit = !!id;

  const sections: IFormSection[] = [
    {
      title: "Transaction Details",
      fields: [
        {
          field_name: "phone_number",
          label: "Phone Number",
          type: "text",
          props: {
            required: true,
            placeholder: "Enter phone number (e.g., 256784306486)",
            disabled: isEdit,
            pattern: "^256[0-9]{9}$",
          },
        },
        {
          field_name: "amount",
          label: "Amount",
          type: "number",
          props: {
            required: true,
            placeholder: "Enter transaction amount",
            min: 0,
            step: 1,
          },
        },
        {
          field_name: "txn_type",
          label: "Transaction Type",
          type: "select",
          props: {
            required: true,
            options: [
              { value: "pre_payment", label: "Pre Payment" },
              { value: "parking_payment", label: "Parking Payment" },
            ],
          },
        },
        {
          field_name: "car_number",
          label: "Car Number",
          type: "text",
          props: {
            required: false,
            placeholder: "Enter car number (e.g., UAB1230)",
            disabled: false,
          },
        },
      ],
    },
  ];

  const title = "Advance Transactions Management";
  const redirect = getRegister(URL_ADVANCES_TRANSACTIONS);

  return {
    sections,
    title,
    redirect,
    loading,
    data,
  };
};

export const AdvancesTransactionsForm = ({
  form,
  error,
  updateFormValue,
  onChange,
}: any) => {
  const { sections } = useAdvancesTransactionsForm({
    form,
    updateFormValues: updateFormValue,
  });

  return (
    <div>
      {sections.map((section: IFormSection, index: number) => (
        <div key={index}>
          <h3>{section.title}</h3>
          <FormFields
            form={form}
            error={error}
            updateFormValue={updateFormValue}
            onChange={onChange}
            fields={section.fields}
          />
        </div>
      ))}
    </div>
  );
};
